import { Component, Input } from '@angular/core';
import { BaseComponent } from '@stockaid/core';
import { RepricingItem } from 'src/app/core/utils/recommendation.utils';
import { COMMON_FORMAT_DATE } from 'src/app/core/constants/format-date.constant';
import { REPRICING_INVENTORY_STATUS_STATE_DISPLAY } from '../forecast-rxdata-table/history-logs-ui/log-context/log-context.constant';
import { ItemPriceState } from 'src/app/core/models/history-log';
import { InventoryRow } from '../product-ad-inventory-analysis-modal/product-ad-inventory-analysis-modal.constant';
import { InventorySourcePreference } from 'src/app/core/infrastructure/enums/inventory-source-preference.enum';

@Component({
  selector: 'app-repricing-recommend-analysis-content',
  templateUrl: './repricing-recommend-analysis-content.component.html',
  styleUrls: ['./repricing-recommend-analysis-content.component.scss'],
})
export class RepricingRecommendAnalysisContentComponent extends BaseComponent {
  @Input() displayCurrency!: { code: string; symbol: string };
  @Input() isFitGrid: boolean = false;

  private _repricingItem!: RepricingItem;
  @Input() set repricingItem(val: RepricingItem) {
    this._repricingItem = val;
    if (val) {
      this.buildInventoryRows();
    }
  }

  get repricingItem(): RepricingItem {
    return this._repricingItem;
  }

  readonly FORMAT_DATE = COMMON_FORMAT_DATE;
  readonly REPRICING_INVENTORY_STATUS_STATE_DISPLAY =
    REPRICING_INVENTORY_STATUS_STATE_DISPLAY;

  get isSpecialState(): boolean {
    return [ItemPriceState.DEAD_STOCK, ItemPriceState.MISSING_PRICE].includes(
      this.repricingItem?.state,
    );
  }

  constructor() {
    super();
  }

  inventoryRows: InventoryRow[] = [];

  private buildInventoryRows() {
    const snap = this.repricingItem?.entitySnapshot;
    if (!snap) {
      this.inventoryRows = [];
      return;
    }

    const pref = snap.inventorySourcePreference;
    const isAwdActive = Boolean(snap.isUsingAwdInventoryQty);

    // Rules aligned with calculateOnHandValue logic & FBM constraints:
    const showFbm =
      pref === InventorySourcePreference.FBM ||
      pref === InventorySourcePreference.FBAPlusFBM;
    const showFba =
      pref === InventorySourcePreference.FBA ||
      pref === InventorySourcePreference.FBAPlusFBM;
    const showAwd = isAwdActive;

    const infoParts: string[] = [];

    if (showFba) {
      infoParts.push(`FBA (${(snap.inboundShipped || 0) + (snap.inboundReceiving || 0)})`);
    }

    if (showAwd) {
      infoParts.push(`AWD (${(snap.inboundShippedAwd || 0) + (snap.inboundReceivingAwd || 0)})`);
    }

    const inboundInfo = infoParts.length > 0 ? infoParts.join(' + ') : undefined;

    this.inventoryRows = [
      {
        label: 'On Hand FBA',
        value: snap.onHand || 0,
        hidden: !showFba,
      },
      {
        label: 'On Hand FBM',
        value: snap.onHandFbm || 0,
        hidden: !showFbm,
      },
      {
        label: 'FC Transfer',
        value: snap.inboundFcTransfer || 0,
      },
      {
        label: 'AWD',
        value: showAwd ? snap.awdInventoryQty || 0 : 0,
        hidden: !showAwd,
      },
      {
        label: 'Inbound',
        value: snap.inbound || 0,
        info: inboundInfo,
      },
      {
        label: 'Total',
        value: this.repricingItem?.context?.inventory || 0,
        isTotalRow: true,
      },
    ];
  }

  getResultClass() {
    switch (this.repricingItem?.state) {
      case ItemPriceState.UNDERSTOCK_CRITICAL:
        return 'seg-under-crit';
      case ItemPriceState.UNDERSTOCK_MODERATE:
        return 'seg-under-mod';
      case ItemPriceState.UNDERSTOCK_MILD:
        return 'seg-under-mild';
      case ItemPriceState.BALANCED:
        return 'seg-balanced';
      case ItemPriceState.OVERSTOCK_HIGH:
        return 'seg-over-high';
      case ItemPriceState.OVERSTOCK_CRITICAL:
        return 'seg-over-crit';
      default:
        return 'not-valid';
    }
  }
}
